import React, { useRef, useState } from "react";
import { Link } from "react-router-dom";
import {
  FiArrowRight, FiArrowUpRight, FiBox, FiCalendar, FiCheck,
  FiCheckCircle, FiLayers, FiMail, FiMapPin, FiPackage, FiPhone,
} from "react-icons/fi";
import InquirySelect from "./InquirySelect";
import laundrySheets from "../../Assets/products/klean-leaf-laundry-detergent-sheets-packaging-and-sheets.webp";
import tablets from "../../Assets/products/klean-leaf-dishwasher-tablets.webp";
import pods from "../../Assets/products/klean-leaf-laundry-detergent-pods.webp";
import './Contact.scss';
import Seo, { breadcrumbSchema, organizationSchema, webPageSchema } from "../Seo/Seo";
import {
  CONTACT_FORM_EMAIL,
  CONTACT_FORM_SUCCESS_MESSAGE,
  submitContactForm,
} from "../../utils/contactFormSubmission";
import { createInquirySubject } from "../../utils/inquirySubject";
import {
  PRODUCT_INQUIRY_FIELD_NAMES,
  normalizeProductInquiryFields,
  estimatedOrderQuantityOptions,
  needOptions,
  packagingTypeOptions,
} from "../../utils/laundrySheetInquiryFields";

const contactTitle = "Contact Klean Leaf | Private Label Laundry Detergent Sheets";
const contactDescription = "Start your private label project with Klean Leaf. Request laundry detergent sheet samples, packaging support and quotes, or discuss tablets, pods and home-care products.";
const laundryPath = "/products?category=sheets";
const productOptions = ["Sheets", "Pods", "Tablets", "Powder", "Others"];
const projectGuide = [
  { Icon: FiLayers, title: "Product", detail: "What do you want to make?" },
  { Icon: FiBox, title: "Quantity", detail: "Estimated first order." },
  { Icon: FiPackage, title: "Packaging", detail: "Box, pouch, envelope or other format." },
  { Icon: FiCalendar, title: "Timeline", detail: "When do you want to launch?" },
];
const productEntries = [
  { title: "Laundry Sheets", detail: "Our specialty", image: laundrySheets,
    alt: "Private label laundry detergent sheets with paper box packaging", to: laundryPath },
  { title: "Tablets", detail: "Concentrated cleaning", image: tablets,
    alt: "Dishwasher cleaning tablets with Klean Leaf packaging", to: "/products?category=tablets" },
  { title: "Pods & More", detail: "More formats for your brand", image: pods,
    alt: "Laundry detergent pods with private label packaging", to: "/products?category=pods" },
];

function ContactForm() {
  const [status, setStatus] = useState(null);
  const [emailError, setEmailError] = useState("");
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [selectedProducts, setSelectedProducts] = useState([]);
  const [orderQuantity, setOrderQuantity] = useState("Not sure yet");
  const [packagingType, setPackagingType] = useState("Need Recommendation");
  const [selectedNeeds, setSelectedNeeds] = useState([]);
  const formRef = useRef(null);
  const nameRef = useRef(null);

  const validateEmail = (email) => /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email);

  const onSubmit = async (event) => {
    event.preventDefault();
    if (isSubmitting) return;
    setStatus(null);
    setEmailError("");
    const form = event.currentTarget;
    const formData = new FormData(form);
    if (!validateEmail(formData.get("email"))) {
      setEmailError("Please enter a valid email address.");
      form.elements.email.focus();
      return;
    }
    normalizeProductInquiryFields(formData);
    formData.append("from_name", "Klean Leaf Website");
    formData.set("subject", createInquirySubject(formData));
    setIsSubmitting(true);
    try {
      await submitContactForm(formData);
      setStatus({ type: "success" });
      form.reset();
      setSelectedProducts([]);
      setOrderQuantity("Not sure yet");
      setPackagingType("Need Recommendation");
      setSelectedNeeds([]);
    } catch (error) {
      console.error("Contact form submission failed:", error);
      setStatus({ type: "error" });
    } finally {
      setIsSubmitting(false);
    }
  };

  const requestSamples = () => {
    setSelectedNeeds((needs) => needs.includes("Samples") ? needs : [...needs, "Samples"]);
    setStatus(null);
    nameRef.current?.focus({ preventScroll: true });
    formRef.current?.scrollIntoView({
      behavior: window.matchMedia("(prefers-reduced-motion: reduce)").matches ? "auto" : "smooth",
      block: "start",
    });
  };

  return (
    <main className="contact-section contact-page">
      <Seo title={contactTitle} description={contactDescription} path="/contact" schema={[
        organizationSchema,
        webPageSchema({ name: contactTitle, description: contactDescription, path: "/contact", type: "ContactPage", primaryImage: laundrySheets }),
        breadcrumbSchema([{ name: "Home", path: "/" }, { name: "Contact", path: "/contact" }]),
      ]} />
      <div className="contact-inquiry-area">
        <div className="contact-container">
          <header className="contact-intro">
            <div>
              <p className="contact-eyebrow"><span aria-hidden="true" />Let’s Build Your Product</p>
              <h1>Start Your <br /><span>Private Label Project</span></h1>
            </div>
            <div className="contact-intro-copy">
              <p>Tell us what you’re looking to make, and our team can help with product options, packaging, samples, pricing and next steps.</p>
              <p className="contact-positioning">Specializing in <strong>private label laundry detergent sheets</strong>, with additional tablets, pods and home-care formats available.</p>
              <Link to={laundryPath} className="contact-text-link">Explore Laundry Detergent Sheets <FiArrowUpRight aria-hidden="true" /></Link>
            </div>
          </header>
          <div className="contact-main-grid">
            <div className="contact-form-panel">
              <div className="contact-form-heading">
                <h2>Your Project Inquiry</h2><span>* Required fields</span>
              </div>
              <form ref={formRef} id="project-inquiry" onSubmit={onSubmit} className="contact-form" aria-label="Project inquiry" aria-busy={isSubmitting}>
                <fieldset>
                  <legend><span aria-hidden="true">01</span>Your Information</legend>
                  <div className="contact-field-grid">
                    <div className="contact-field">
                      <label htmlFor="contact-name">Name *</label>
                      <input ref={nameRef} id="contact-name" type="text" name="name" autoComplete="name" required />
                    </div>
                    <div className="contact-field">
                      <label htmlFor="contact-email">Email *</label>
                      <input id="contact-email" type="email" name="email" autoComplete="email" required
                        aria-describedby={emailError ? "contact-email-error" : undefined}
                        aria-invalid={emailError ? "true" : "false"}
                        onChange={(event) => setEmailError(validateEmail(event.target.value) ? "" : "Please enter a valid email address.")} />
                      {emailError && <span id="contact-email-error" className="error-message">{emailError}</span>}
                    </div>
                    <div className="contact-field contact-field-wide">
                      <label htmlFor="contact-company">Company</label>
                      <input id="contact-company" type="text" name="company" autoComplete="organization" />
                    </div>
                  </div>
                </fieldset>
                <fieldset>
                  <legend><span aria-hidden="true">02</span>Your Project</legend>
                  <div className="contact-field-grid">
                    <div className="contact-field">
                      <label htmlFor="contact-product-name">Product of Interest</label>
                      <InquirySelect id="contact-product-name" name={PRODUCT_INQUIRY_FIELD_NAMES.productName}
                        mode="multiple" placeholder="Select Product types"
                        value={selectedProducts} onChange={setSelectedProducts} options={productOptions} />
                    </div>
                    <div className="contact-field">
                      <label htmlFor="contact-expected-launch-date">Expected Launch Date</label>
                      <input id="contact-expected-launch-date" type="date" name={PRODUCT_INQUIRY_FIELD_NAMES.expectedLaunchDate} />
                    </div>
                    <div className="contact-field">
                      <label htmlFor="contact-order-quantity">Estimated Order Quantity</label>
                      <InquirySelect id="contact-order-quantity" name={PRODUCT_INQUIRY_FIELD_NAMES.estimatedOrderQuantity}
                        value={orderQuantity} onChange={setOrderQuantity} options={estimatedOrderQuantityOptions} />
                    </div>
                    <div className="contact-field">
                      <label htmlFor="contact-packaging-type">Packaging Type</label>
                      <InquirySelect id="contact-packaging-type" name={PRODUCT_INQUIRY_FIELD_NAMES.packagingType}
                        value={packagingType} onChange={setPackagingType} options={packagingTypeOptions} />
                    </div>
                  </div>
                </fieldset>
                <fieldset>
                  <legend><span aria-hidden="true">03</span>How Can We Help?</legend>
                  <div className="contact-needs">
                    {needOptions.map((option) => (
                      <label className={`contact-need${selectedNeeds.includes(option) ? " is-selected" : ""}`} key={option}>
                        <input type="checkbox" name={PRODUCT_INQUIRY_FIELD_NAMES.needs} value={option}
                          checked={selectedNeeds.includes(option)}
                          onChange={(event) => {
                            const checked = event.target.checked;
                            setSelectedNeeds((needs) => checked ? [...needs, option] : needs.filter((need) => need !== option));
                          }} />
                        <span>{option}</span>
                      </label>
                    ))}
                  </div>
                </fieldset>
                <fieldset>
                  <legend><span aria-hidden="true">04</span>Project Details</legend>
                  <div className="contact-field">
                    <label htmlFor="contact-message">Message *</label>
                    <textarea id="contact-message" name="message" autoComplete="off" required maxLength="2000"
                      placeholder="Tell us about your product, target market or any questions you have." />
                  </div>
                </fieldset>
                <button type="submit" className="contact-button contact-submit" disabled={isSubmitting}>
                  {isSubmitting ? "Sending..." : "Request Samples or Quote"}<FiArrowRight aria-hidden="true" />
                </button>
                <p className="contact-form-note">Our team will review your project and help with next steps.</p>
                {status && (
                  <div className={`form-result ${status.type}`} role={status.type === "error" ? "alert" : "status"}>
                    {status.type === "success" ? <>
                      <FiCheckCircle aria-hidden="true" />
                      <div><h2>Thank You — We Received Your Inquiry</h2><p>{CONTACT_FORM_SUCCESS_MESSAGE}</p>
                        <Link to="/products" className="contact-text-link">Explore Products <FiArrowUpRight aria-hidden="true" /></Link></div>
                    </> : <p>We couldn’t send your message right now. Please try again, or email us directly at <a href={`mailto:${CONTACT_FORM_EMAIL}`}>{CONTACT_FORM_EMAIL}</a>.</p>}
                  </div>
                )}
              </form>
            </div>
            <aside className="contact-sidebar" aria-label="Project guidance and contact information">
              <section className="contact-guide" aria-labelledby="contact-guide-title">
                <p className="contact-eyebrow">A Good Place to Start</p>
                <h2 id="contact-guide-title">Tell Us About <br />Your Project</h2>
                <ul className="contact-guide-list">
                  {projectGuide.map(({ Icon, title, detail }) => <li key={title}>
                    <Icon aria-hidden="true" /><div><h3>{title}</h3><p>{detail}</p></div>
                  </li>)}
                </ul>
                <div className="contact-browse-links">
                  <p>Need help choosing?</p>
                  <Link to="/products" className="contact-text-link">Browse All Products <FiArrowUpRight aria-hidden="true" /></Link>
                  <Link to={laundryPath} className="contact-text-link">Explore Laundry Sheets <FiArrowUpRight aria-hidden="true" /></Link>
                </div>
              </section>
              <section className="contact-team" aria-labelledby="contact-team-title">
                <h2 id="contact-team-title">Talk to Our U.S. Team</h2>
                <address>
                  <span><FiMapPin aria-hidden="true" />Columbus, Ohio</span>
                  <a href={`mailto:${CONTACT_FORM_EMAIL}`}><FiMail aria-hidden="true" />{CONTACT_FORM_EMAIL}</a>
                  <a href="tel:+16149065129"><FiPhone aria-hidden="true" />614.906.5129</a>
                </address>
                <p>Product inquiries, samples, packaging questions and private label projects.</p>
              </section>
              <div className="contact-resource-links">
                <Link to="/products/laundry-detergent-sheets#product-laundry-detergent-sheets-detail-packaging" className="contact-text-link">See Packaging Options <FiArrowUpRight aria-hidden="true" /></Link>
                <Link to="/solution" className="contact-text-link">Explore Private Label Solutions <FiArrowUpRight aria-hidden="true" /></Link>
              </div>
            </aside>
          </div>
          <ul className="contact-trust-strip" aria-label="Project support">
            {["Private Label", "Custom Packaging", "Sample Support"].map((label) => <li key={label}><FiCheck aria-hidden="true" />{label}</li>)}
          </ul>
        </div>
      </div>
      <section className="contact-discovery contact-container" aria-labelledby="contact-products-heading">
        <div className="contact-section-heading">
          <div><p className="contact-eyebrow">Find Your Format</p><h2 id="contact-products-heading">Still Choosing a Product?</h2>
            <p>Explore our product range before starting your inquiry.</p></div>
          <Link to="/products" className="contact-text-link">Explore All Products <FiArrowUpRight aria-hidden="true" /></Link>
        </div>
        <div className="contact-product-grid">
          {productEntries.map(({ title, detail, image, alt, to }, index) => (
            <Link to={to} className={`contact-product${index === 0 ? " contact-product-featured" : ""}`} key={title}>
              <div className="contact-product-photo"><img src={image} alt={alt} loading="lazy" decoding="async" width="640" height="480" /></div>
              <div className="contact-product-caption"><div><span>{detail}</span><h3>{title}</h3></div><FiArrowUpRight aria-hidden="true" /></div>
            </Link>
          ))}
        </div>
      </section>
      <section className="contact-samples contact-container" aria-labelledby="contact-samples-heading">
        <div className="contact-samples-copy"><FiPackage aria-hidden="true" /><div>
          <h2 id="contact-samples-heading">Want to Test Before You Order?</h2>
          <p>Request available product samples to compare formulas, fragrances and formats.</p>
        </div></div>
        <button type="button" className="contact-button" onClick={requestSamples} disabled={isSubmitting}>Ask About Samples <FiArrowUpRight aria-hidden="true" /></button>
      </section>
      <p className="contact-reassurance contact-container">Every project starts with a conversation. <a href={`mailto:${CONTACT_FORM_EMAIL}`}>Talk to our team <FiArrowUpRight aria-hidden="true" /></a></p>
    </main>
  );
}

export default ContactForm;
